import React, { useState, useEffect } from 'react'
import {
  Card,
  Row,
  Col,
  Tag,
  Button,
  Space,
  Table,
  DatePicker,
  Progress,
  Select,
  Typography,
  Empty,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import {
  CalculatorOutlined,
  CopyOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from 'recharts'

import { meterReadingStore } from '../MeterReading/meterReadingStore'
import { allocationStore } from '../Allocation/allocationStore'
import { mappingStore } from '../Mapping/mappingStore'
import { useMasterData } from '@/context/MasterDataContext'

const { Title, Text } = Typography

// Time-of-day category label for a Slot master row, same wording as AllocationForm.
const SLOT_CAT: Record<string, string> = { 'off-peak': 'Off-Peak', normal: 'Normal', peak: 'Peak' }
const SLOT_CHART_COLORS = ['#ef4444', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899']

const isDateInPeriod = (
  dateVal: any,
  period: string,
  range: [Dayjs | null, Dayjs | null] | null
): boolean => {
  if (period === 'all') return true
  if (!dateVal) return true
  const d = dayjs(dateVal)
  if (!d.isValid()) return true

  const now = dayjs()
  if (period === '30d') {
    return d.isAfter(now.subtract(30, 'day').startOf('day')) || d.isSame(now, 'month')
  }
  if (period === '90d') {
    return d.isAfter(now.subtract(90, 'day').startOf('day'))
  }
  if (period === 'fy') {
    const currentYear = now.year()
    const fyStart = now.month() >= 3 ? dayjs(`${currentYear}-04-01`) : dayjs(`${currentYear - 1}-04-01`)
    const fyEnd = fyStart.add(1, 'year').subtract(1, 'day').endOf('day')
    return (d.isAfter(fyStart.startOf('day')) || d.isSame(fyStart.startOf('day'))) && (d.isBefore(fyEnd) || d.isSame(fyEnd))
  }
  if (period === 'custom') {
    if (!range || !range[0] || !range[1]) return true
    return (
      (d.isAfter(range[0].startOf('day')) || d.isSame(range[0].startOf('day'))) &&
      (d.isBefore(range[1].endOf('day')) || d.isSame(range[1].endOf('day')))
    )
  }
  return true
}

const AllocationDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { generators, consumers, slots: masterSlots } = useMasterData()
  const [selectedPeriod, setSelectedPeriod] = useState<string>('30d')
  const [customDateRange, setCustomDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)

  // Live store data
  const [meterReadings, setMeterReadings] = useState(() => meterReadingStore.getAll())
  const [allocations, setAllocations] = useState(() => allocationStore.getAll())
  const [mappings, setMappings] = useState(() => mappingStore.getAll())

  useEffect(() => {
    const sync = () => {
      setMeterReadings(meterReadingStore.getAll())
      setAllocations(allocationStore.getAll())
      setMappings(mappingStore.getAll())
    }
    sync()
    meterReadingStore.refresh().then(sync)
    allocationStore.refresh().then(sync)
    mappingStore.refresh().then(sync)

    window.addEventListener(meterReadingStore.eventName, sync)
    window.addEventListener(allocationStore.eventName, sync)
    window.addEventListener(mappingStore.eventName, sync)

    return () => {
      window.removeEventListener(meterReadingStore.eventName, sync)
      window.removeEventListener(allocationStore.eventName, sync)
      window.removeEventListener(mappingStore.eventName, sync)
    }
  }, [])

  // Filtered arrays based on date period
  const filteredReadings = meterReadings.filter((m) =>
    isDateInPeriod((m as any).readingDate || (m as any).createdAt || (m as any).month, selectedPeriod, customDateRange)
  )
  const filteredAllocations = allocations.filter((a) =>
    isDateInPeriod((a as any).allocationDate || (a as any).createdAt || (a as any).period, selectedPeriod, customDateRange)
  )
  const filteredMappings = mappings.filter((m) =>
    isDateInPeriod((m as any).effectiveFrom || (m as any).createdAt, selectedPeriod, customDateRange)
  )

  // KPI Calculations
  const totalReadingsCount = filteredReadings.length
  const approvedReadingsCount = filteredReadings.filter((m) => m.status === 'Approved').length

  const totalAllocatedUnits = filteredAllocations.reduce(
    (acc, a) => acc + (Number((a as any).totalAllocatedUnits ?? (a as any).totalAllocated ?? (a as any).allocatedUnits) || 0),
    0
  )
  // "Lapsed" = generated but not allocated to any consumer — allocationStore's
  // real `tdLoss` field is exactly totalGenerated - totalAllocated (see its
  // doc comment in allocationStore.ts), not a synthetic field.
  const totalLapsedUnits = filteredAllocations.reduce((acc, a) => acc + (Number((a as any).tdLoss) || 0), 0)
  const totalGenUnits = totalAllocatedUnits + totalLapsedUnits
  const allocationRatio = totalGenUnits ? Math.round((totalAllocatedUnits / totalGenUnits) * 100) : 100

  const activeMappingsCount = filteredMappings.filter((m) => (m as any).status !== 'Inactive').length

  // TOD Slots breakdown — real per-slot generation from each allocation's
  // `generationSlots` (same {slotName, units}[] shape AllocationForm writes),
  // aggregated across the filtered allocations and labelled from the live
  // Slot Master (falls back to bare C1-C5 if the master is empty).
  const slotDefs = (masterSlots.filter(s => s.status === 'active').length
    ? masterSlots.filter(s => s.status === 'active')
    : (['C1', 'C2', 'C3', 'C4', 'C5'] as const).map(code => ({ slotCode: code, category: 'normal' as const, startTime: '', endTime: '' })))
    .slice()
    .sort((a, b) => {
      const na = parseInt((a.slotCode || '').replace(/\D/g, ''), 10)
      const nb = parseInt((b.slotCode || '').replace(/\D/g, ''), 10)
      return (isNaN(na) ? Number.MAX_SAFE_INTEGER : na) - (isNaN(nb) ? Number.MAX_SAFE_INTEGER : nb)
    })

  const todSlotData = slotDefs.map((s, i) => ({
    slot: `${s.slotCode} ${SLOT_CAT[s.category] || s.category}${s.startTime ? ` (${s.startTime}-${s.endTime})` : ''}`,
    units: filteredAllocations.reduce(
      (acc, a: any) => acc + (a.generationSlots || []).reduce(
        (sAcc: number, gs: { slotName: string; units: number }) => sAcc + (gs.slotName === s.slotCode ? (Number(gs.units) || 0) : 0),
        0
      ),
      0
    ),
    fill: SLOT_CHART_COLORS[i % SLOT_CHART_COLORS.length],
  }))
  const hasTodData = todSlotData.some(d => d.units > 0)

  // Ratio Pie Data — real numbers only, no fabricated fallback.
  const ratioPieData = [
    { name: 'Allocated Power', value: totalAllocatedUnits, color: '#10b981' },
    { name: 'Lapsed / Unallocated', value: totalLapsedUnits, color: '#ef4444' },
  ]
  const hasRatioData = totalGenUnits > 0

  // Recent Allocations Columns
  const allocationColumns = [
    {
      title: 'Allocation #',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => (
        <span style={{ fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: '#001A4D' }}>
          {id}
        </span>
      ),
    },
    {
      title: 'Generator',
      dataIndex: 'generatorName',
      key: 'generatorName',
      render: (val: string, r: any) => val || r.generatorId || 'Generator',
    },
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
      render: (val: string) => val || '—',
    },
    {
      title: 'Allocated Units (kWh)',
      key: 'allocatedUnits',
      render: (_: any, r: any) => (
        <strong style={{ color: '#10b981' }}>
          {Number(r.totalAllocatedUnits || r.allocatedUnits || 0).toLocaleString()} kWh
        </strong>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'Approved' || status === 'Finalized' ? 'success' : 'processing'}>
          {status || 'Draft'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, r: any) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate('/allocations')}
        >
          View
        </Button>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px', background: 'var(--surface-0)', minHeight: '100vh' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0, color: 'var(--brand-primary)', fontWeight: 700 }}>
            Allocation Dashboard
          </Title>
          <Text style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            Meter Readings, Energy Allocations Engine, TOD Slot Distribution & Generator Mappings
          </Text>
        </div>

        <Space wrap style={{ alignItems: 'center' }}>
          <Select
            value={selectedPeriod}
            onChange={(val) => {
              setSelectedPeriod(val)
              if (val !== 'custom') {
                setCustomDateRange(null)
              }
            }}
            style={{ minWidth: 140 }}
            options={[
              { label: 'Current Period', value: '30d' },
              { label: 'This Quarter', value: '90d' },
              { label: 'Financial Year', value: 'fy' },
              { label: 'Custom Range', value: 'custom' },
              { label: 'All Time', value: 'all' },
            ]}
          />
          {selectedPeriod === 'custom' && (
            <DatePicker.RangePicker
              value={customDateRange}
              onChange={(dates) => setCustomDateRange(dates as [Dayjs | null, Dayjs | null])}
              style={{ width: 250 }}
              format="YYYY-MM-DD"
              placeholder={['Start Date', 'End Date']}
            />
          )}
          <Button type="primary" icon={<CalculatorOutlined />} onClick={() => navigate('/allocations/new')}>
            New Allocation
          </Button>
          <Button icon={<CopyOutlined />} onClick={() => navigate('/meter-readings/new')}>
            Add Meter Reading
          </Button>
          <Button icon={<SwapOutlined />} onClick={() => navigate('/mappings/new')}>
            New Mapping
          </Button>
        </Space>
      </div>

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={12} xl={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #3b82f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Allocated Units</Text>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {totalAllocatedUnits.toLocaleString()} kWh
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'nowrap' }}>
                  Efficiency Ratio: <strong>{allocationRatio}%</strong>
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CalculatorOutlined style={{ fontSize: 22, color: '#3b82f6' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={12} xl={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #ef4444' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Lapsed Power</Text>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {totalLapsedUnits.toLocaleString()} kWh
                </div>
                <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Unallocated / Surplus
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ExclamationCircleOutlined style={{ fontSize: 22, color: '#ef4444' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={12} xl={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #10b981' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Meter Readings</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{totalReadingsCount}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'nowrap' }}>
                  Approved: <strong>{approvedReadingsCount}</strong>
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CopyOutlined style={{ fontSize: 22, color: '#10b981' }} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={12} xl={6}>
          <Card style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', borderLeft: '4px solid #8b5cf6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <Text style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Generator Mappings</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#8b5cf6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeMappingsCount}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'nowrap' }}>
                  Generators: <strong>{generators.length}</strong> | Consumers: <strong>{consumers.length}</strong>
                </div>
              </div>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(139,92,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <SwapOutlined style={{ fontSize: 22, color: '#8b5cf6' }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Visual Analytics Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={14} lg={13}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 16 }}>TOD Slot Allocated Units Breakdown (C1 to C5)</span>}
            style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', height: '100%' }}
          >
            <div style={{ height: 260 }}>
              {hasTodData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={todSlotData} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="slot" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="units" radius={[6, 6, 0, 0]}>
                      {todSlotData.map((entry, index) => (
                        <Cell key={`cell-tod-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No slot-wise generation for this period" style={{ paddingTop: 60 }} />
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={10} lg={11}>
          <Card
            title={<span style={{ fontWeight: 700, fontSize: 16 }}>Allocation Ratio (%)</span>}
            style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', height: '100%' }}
          >
            <div style={{ height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {hasRatioData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={ratioPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {ratioPieData.map((entry, index) => (
                        <Cell key={`cell-r-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No allocations for this period" />
              )}
            </div>
            {hasRatioData && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                {ratioPieData.map((d) => (
                  <span key={d.name} style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color }} />
                    {d.name}
                  </span>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Allocation List Table */}
      <Card
        title={<span style={{ fontWeight: 700, fontSize: 16 }}>Recent Unit Allocation Batches</span>}
        extra={<Button type="link" onClick={() => navigate('/allocations')}>View All Allocations ({filteredAllocations.length})</Button>}
        style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}
      >
        <Table
          dataSource={filteredAllocations.slice(0, 5)}
          columns={allocationColumns}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 700 }}
        />
      </Card>
    </div>
  )
}

export default AllocationDashboard
